import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { OverlayContainer } from '@angular/cdk/overlay';
import { BehaviorSubject } from 'rxjs';
import { ITheme } from '../theme/theme.interface';
import { IThemeOption } from '../theme/theme-option.interface';
import { ThemeMode } from '../theme/theme-mode.type';
import { ThemeNormalizer } from '../theme/theme-normalizer';
import { ThemeCss } from '../theme/theme-css';
import { ThemeRegistry } from '../theme/theme-registry';

export type { ThemeMode } from '../theme/theme-mode.type';

/**
 * Two independent choices: the mode (light / dark) and the theme (a JSON file
 * with colours, spacing, radii, borders and fonts, IC-840). Both are kept in
 * localStorage.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  static readonly STYLE_ELEMENT_ID = 'app-theme-tokens';
  private static readonly _storageKey = 'theme';
  private static readonly _themeIdStorageKey = 'theme-id';

  private readonly _themeSubject = new BehaviorSubject<ThemeMode>('light');
  readonly theme$ = this._themeSubject.asObservable();

  private readonly _themeIdSubject = new BehaviorSubject<string>(ThemeRegistry.DEFAULT_THEME.id);
  readonly themeId$ = this._themeIdSubject.asObservable();

  private readonly _themesSubject = new BehaviorSubject<IThemeOption[]>([ThemeRegistry.DEFAULT_OPTION]);
  /** The themes the picker offers; filled from public/themes/index.json. */
  readonly themes$ = this._themesSubject.asObservable();

  private readonly _loaded = new Map<string, ITheme>([[ThemeRegistry.DEFAULT_THEME.id, ThemeRegistry.DEFAULT_THEME]]);

  constructor(
    private overlayContainer: OverlayContainer,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  init(): void {
    // The default tokens go in on the server too, so the first paint is already themed.
    this._writeTokens(ThemeRegistry.DEFAULT_THEME);
    if (!this._isBrowser()) {
      // On server, just set subject without touching DOM
      this._themeSubject.next('light');
      return;
    }
    const stored = this._safeStorageGet(ThemeService._storageKey) as ThemeMode | null;
    const isDarkPreferred =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initial: ThemeMode = stored ?? (isDarkPreferred ? 'dark' : 'light');
    this._applyMode(initial);

    void this._loadThemeList();
    // A page embedded elsewhere (the public booking iframe, IC-855) can ask for its own
    // theme with ?theme=<id>; it applies to that page only and is not remembered.
    const requestedThemeId = this._queryThemeId();
    if (requestedThemeId) {
      void this.setThemeId(requestedThemeId, { isRemembered: false });
      return;
    }
    const storedThemeId = this._safeStorageGet(ThemeService._themeIdStorageKey);
    if (storedThemeId && storedThemeId !== ThemeRegistry.DEFAULT_THEME.id) {
      void this.setThemeId(storedThemeId);
    }
  }

  toggle(): void {
    const next: ThemeMode = this.currentTheme === 'dark' ? 'light' : 'dark';
    this._applyMode(next);
  }

  setTheme(mode: ThemeMode): void {
    this._applyMode(mode);
  }

  /**
   * Switches to the theme with this id: the bundled default, or
   * public/themes/<id>.json. A theme that cannot be loaded leaves the current
   * one in place and resolves to false.
   */
  async setThemeId(id: string, options: { isRemembered?: boolean } = {}): Promise<boolean> {
    const theme = this._loaded.get(id) ?? (await this._fetchTheme(ThemeRegistry.url(id)));
    if (!theme) return false;
    this._applyThemeDefinition(theme, options.isRemembered ?? true);
    return true;
  }

  /** Applies a theme given as JSON (already parsed), e.g. one being designed. */
  applyThemeJson(json: unknown): ITheme {
    const theme = this._normalize(json);
    this._loaded.set(theme.id, theme);
    this._applyThemeDefinition(theme);
    return theme;
  }

  private _applyThemeDefinition(theme: ITheme, isRemembered = true): void {
    this._writeTokens(theme);
    this._themeIdSubject.next(theme.id);
    if (isRemembered) this._safeStorageSet(ThemeService._themeIdStorageKey, theme.id);
  }

  private _queryThemeId(): string | null {
    try {
      const id = new URLSearchParams(window.location.search).get('theme');
      return id && /^[a-z0-9][a-z0-9-]*$/i.test(id) ? id : null;
    } catch {
      return null;
    }
  }

  private _normalize(json: unknown): ITheme {
    const { theme, warnings } = ThemeNormalizer.normalize(json, ThemeRegistry.DEFAULT_THEME);
    if (warnings.length) console.warn(`Tema "${theme.id}":`, warnings);
    return theme;
  }

  private async _fetchTheme(url: string): Promise<ITheme | null> {
    const json = await this._fetchJson(url);
    if (json === null) return null;
    const theme = this._normalize(json);
    this._loaded.set(theme.id, theme);
    return theme;
  }

  private async _loadThemeList(): Promise<void> {
    const list = await this._fetchJson(ThemeRegistry.INDEX_URL);
    if (!Array.isArray(list)) return;
    const options = (list as Partial<IThemeOption>[]).filter(
      (o): o is IThemeOption => !!o && typeof o.id === 'string' && typeof o.name === 'string',
    );
    const isDefaultListed = options.some((o) => o.id === ThemeRegistry.DEFAULT_THEME.id);
    this._themesSubject.next(isDefaultListed ? options : [ThemeRegistry.DEFAULT_OPTION, ...options]);
  }

  private async _fetchJson(url: string): Promise<unknown> {
    if (!this._isBrowser() || typeof fetch !== 'function') return null;
    try {
      const response = await fetch(url, { headers: { accept: 'application/json' } });
      if (!response.ok) return null;
      return (await response.json()) as unknown;
    } catch {
      return null;
    }
  }

  /** One <style> in <head> holds the active theme's tokens; switching rewrites it. */
  private _writeTokens(theme: ITheme): void {
    const head = this.document?.head;
    if (!head) return;
    let style = this.document.getElementById(ThemeService.STYLE_ELEMENT_ID) as HTMLStyleElement | null;
    if (!style) {
      style = this.document.createElement('style');
      style.id = ThemeService.STYLE_ELEMENT_ID;
      // First in <head>, so the app's own stylesheets still win on equal specificity.
      head.insertBefore(style, head.firstChild);
    }
    style.textContent = ThemeCss.toCss(theme);
    this.document.documentElement.setAttribute('data-theme-id', theme.id);
  }

  private _applyMode(mode: ThemeMode): void {
    this._themeSubject.next(mode);
    this._safeStorageSet(ThemeService._storageKey, mode);
    if (!this._isBrowser()) return;
    const root = document.documentElement;
    if (mode === 'dark') {
      root.classList.add('dark');
      root.setAttribute('data-theme', 'dark');
    } else {
      root.classList.remove('dark');
      root.setAttribute('data-theme', 'light');
    }
    // Sync Angular CDK Overlay container (menus, dialogs, etc.)
    try {
      const overlayEl = this.overlayContainer.getContainerElement();
      overlayEl.classList.toggle('dark', mode === 'dark');
      overlayEl.setAttribute('data-theme', mode);
    } catch {
      // ignore (likely not in browser)
    }
  }

  private _isBrowser(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }

  private _safeStorageGet(key: string): string | null {
    try {
      if (typeof localStorage === 'undefined') return null;
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private _safeStorageSet(key: string, value: string): void {
    try {
      if (typeof localStorage === 'undefined') return;
      localStorage.setItem(key, value);
    } catch {
      // ignore
    }
  }

  get currentTheme(): ThemeMode {
    return this._themeSubject.value;
  }

  get currentThemeId(): string {
    return this._themeIdSubject.value;
  }
}
